/**
 * Event Day staff API (tables, waiters, orders, calls, activity, hubs) on the canonical project-first mount:
 * /event-day/:project_id/... (ans-backend, kal shape). It sends the signed-in session token and the active project; the old
 * web-key-only requests were never given a project by the server and are gone. Ids are uuid strings.
 *
 * The door (guest list, check-in, walk-ins, favours) has its own client: services/DoorApiClient.ts.
 */
import { getApiKey, getApiUrl } from './PlanningApiClient';
import { staffCredentials } from './DoorApiClient';

/** A non-2xx answer. `body` is the parsed JSON when the server sent one (for example `{ error, code }`). */
export class EventDayApiError extends Error {
  constructor(readonly path: string, readonly status: number, readonly body: unknown) {
    super(`EventDayAPI ${path} → ${status}`);
    this.name = 'EventDayApiError';
  }
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const creds = staffCredentials();
  if (!creds) throw new EventDayApiError(path, 401, null);
  const res = await fetch(`${getApiUrl()}/event-day/${creds.projectId}${path}`, {
    ...options,
    cache: 'no-store',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': getApiKey(),
      Authorization: `Bearer ${creds.token}`,
      ...(options.headers as Record<string, string> || {}),
    },
  });
  if (!res.ok) throw new EventDayApiError(path, res.status, await res.json().catch(() => null));
  return res.json();
}

/** Handshake credentials for the live socket: the web key, plus the session token. */
export function getSocketAuth(): { api_key: string; token?: string } {
  const creds = staffCredentials();
  return { api_key: getApiKey(), ...(creds ? { token: creds.token } : {}) };
}

/** What a screen should say about a failed call, in plain words. 'notSetUp' is the server saying a hand-run migration has not been applied yet. */
export type EventDayProblem = { kind: 'notSetUp' | 'forbidden' | 'signedOut' | 'offline' | 'error'; message: string };

export function describeEventDayError(err: unknown): EventDayProblem {
  if (err instanceof EventDayApiError) {
    const code = (err.body as { code?: string } | null)?.code;
    if (err.status === 503 && code === 'not_set_up') return { kind: 'notSetUp', message: (err.body as { error?: string }).error ?? 'This part of Event Day is not set up yet.' };
    if (err.status === 401) return { kind: 'signedOut', message: 'Your session has ended. Please sign in again.' };
    if (err.status === 403) return { kind: 'forbidden', message: "You don't have permission to do that." };
    const text = (err.body as { error?: string } | null)?.error;
    return { kind: 'error', message: typeof text === 'string' && text.length < 200 ? text : 'Something went wrong on our side. Please try again in a moment.' };
  }
  return { kind: 'offline', message: "Couldn't reach the server. Check your connection and try again." };
}

export const eventDayApi = {
  getBaseUrl: () => getApiUrl(),

  // Tables
  getTables: (eventId?: string) => request<{ tables: any[] }>(eventId ? `/tables?event_id=${encodeURIComponent(eventId)}` : '/tables'),
  getTableGuests: (tableId: string, eventId?: string) =>
    request<{ guests: any[] }>(`/tables/${tableId}/guests${eventId ? `?event_id=${encodeURIComponent(eventId)}` : ''}`),

  // Orders (kitchen and bar)
  getOrders: () => request<{ orders: any[] }>('/orders'),
  updateOrder: (orderId: string, status: string) =>
    request(`/orders/${orderId}/status`, { method: 'PATCH', body: JSON.stringify({ status }) }),
  submitOrder: (tableId: string, waiterId: string | null, items: { name: string; quantity?: number; notes?: string }[], notes: string, vendor: 'food' | 'bar' = 'food', idempotencyKey?: string) =>
    request('/orders', {
      method: 'POST',
      body: JSON.stringify({
        table_id: tableId, ...(waiterId ? { waiter_id: waiterId } : {}), vendor, items,
        ...(notes.trim() ? { notes: notes.trim() } : {}), ...(idempotencyKey ? { idempotency_key: idempotencyKey } : {}),
      }),
    }),

  // Activity feed
  getActivity: (hours = 4, limit = 50) =>
    request<{ events: Array<{ id: string; type: string; text: string; timestamp: string }> }>(`/activity?hours=${hours}&limit=${limit}`),

  // Call queue
  getCalls: () => request<{ calls: any[] }>('/call-queue?resolved=false'),
  resolveCall: (callId: string) => request(`/call-queue/${callId}/resolve`, { method: 'PATCH' }),

  // Waiter call hubs (ESP32 + BLE watch relays) — dispatcher/system status view
  getHubs: () => request<{ hubs: Array<{
    hub_id: string;
    ip: string;
    watch_macs: string[];
    registered_at: string;
    last_seen_at: string;
    online: boolean;
  }> }>('/hubs'),

  // Tables CRUD (planner or above)
  createTable: (data: { number: number; name?: string | null; section?: string | null; capacity?: number; floor_plan_id?: string | null; venue_id?: string | null; event_id?: string | null }) =>
    request<any>('/tables', { method: 'POST', body: JSON.stringify(data) }),
  updateTable: (id: string, data: { number?: number; name?: string | null; section?: string | null; capacity?: number; floor_plan_id?: string | null; venue_id?: string | null; event_id?: string | null }) =>
    request<any>(`/tables/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteTable: (id: string) => request<any>(`/tables/${id}`, { method: 'DELETE' }),

  // Waiters
  getWaiters: () => request<{ waiters: any[] }>('/waiters'),
  createWaiter: (data: { name: string; pin: string; phone?: string; role?: string }) =>
    request<any>('/waiters', { method: 'POST', body: JSON.stringify(data) }),
  updateWaiter: (id: string, data: { name?: string; pin?: string; phone?: string; active?: boolean; role?: string }) =>
    request<any>(`/waiters/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteWaiter: (id: string) => request<any>(`/waiters/${id}`, { method: 'DELETE' }),
  loginWaiter: (name: string, pin: string) =>
    request<{ token: string; waiter: any }>('/waiters/auth', { method: 'POST', body: JSON.stringify({ name, pin }) }),
  getWaiterTables: (waiterId: string) => request<{ tables: any[] }>(`/waiters/${waiterId}/tables`),
  assignWaiterTables: (waiterId: string, tableIds: string[]) =>
    request<any>(`/waiters/${waiterId}/tables`, { method: 'POST', body: JSON.stringify({ table_ids: tableIds }) }),
};
